/* eslint-disable @typescript-eslint/no-misused-promises */
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridWrapper,
  SmartAutoComplete,
  SmartCheckbox,
  SmartDatePicker,
  SmartForm,
  SmartTextField,
  usePopup,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FileStateEnum,
  MapAreaTypeEnum,
  ReturnNodeDto,
  SeaSurveyDetailsDto,
  useMapAreaApi,
  useSeaSurveyApi,
} from "~/api/GeneratedApi";
import FileExplorer from "~/components/file-explorer/FileExplorer";
import { FileImportDialog } from "~/components/FileImportDialog";
import { LinearProgressWithLabel } from "~/components/LinearProgressWithLabel";
import LoadingBox from "~/components/LoadingBox";
import useAuth from "~/utils/hooks/useAuth";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import ComparisonLevelAutocomplete from "./ComparisonLevelAutocomplete";
import CoordinateSystemAutocomplete from "./CoordinateSystemAutocomplete";
import SecurityClassificationAutocomplete from "./SecurityClassificationAutocomplete";
import SurveyMethodAutocomplete from "./SurveyMethodAutocomplete";
import { useFileExplorerDialog } from "./useFileExplorerDialog";

const dataToForm = (
  d: SeaSurveyDetailsDto | undefined,
): SeaSurveyFormInputs => {
  return {
    name: d?.name ?? "",
    description: d?.description ?? "",
    archived: d?.archived ?? false,
    surveyStartTimestamp: d?.surveyStartTimestamp ?? new Date(),
    surveyEndTimestamp: d?.surveyEndTimestamp ?? new Date(),
    surveyor: d?.surveyor ?? "",
    vessel: d?.vessel ?? "",
    customer: d?.customer ?? "",
    contact: d?.contact ?? "",
    designer: d?.designer ?? "",
    expert: d?.expert ?? "",
    comparisonLevelId: d?.comparisonLevelId ?? null,
    comparisonLevelOffsetMm: d?.comparisonLevelOffsetMm?.toString() ?? "0",
    sizeSquareMeters: d?.sizeSquareMeters ?? "",
    projectManager: d?.projectManager ?? "",
    releaseTimestamp: d?.releaseTimestamp ?? null,
    coordinateSystemId: d?.coordinateSystemId ?? null,
    surveyMethodId: d?.surveyMethodId ?? null,
    securityClassificationId: d?.securityClassificationId ?? null,
    mapAreaIds: d?.mapAreas?.map((m) => m.mapAreaId) ?? [],
    fileIds: d?.files?.map((f) => f.id) ?? null,
    fileCount: d?.fileCount ?? 0,
    merge: d?.merge ?? false,
  };
};

export interface SeaSurveyFormInputs {
  name: string;
  description: string;
  archived: boolean;
  surveyStartTimestamp: Date;
  surveyEndTimestamp: Date;
  surveyor: string;
  vessel: string;
  sizeSquareMeters: number | null | string;
  customer: string;
  contact: string;
  surveyMethodId: number | null;
  comparisonLevelId: number | null;
  comparisonLevelOffsetMm: string;
  coordinateSystemId: number | null;
  projectManager: string;
  designer: string;
  expert: string;
  securityClassificationId: number | null;
  releaseTimestamp: Date | null;
  mapAreaIds: number[];
  fileIds: number[] | null;
  fileCount: number;
  merge: boolean;
}
function msToHMS(milliSeconds: number) {
  const ms = Math.abs(milliSeconds);
  let seconds: number | string = Math.floor((ms / 1000) % 60);
  let minutes: number | string = Math.floor((ms / (1000 * 60)) % 60);
  let hours: number | string = Math.floor((ms / (1000 * 60 * 60)) % 24);
  hours = hours < 10 ? `0${hours}` : hours;
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  seconds = seconds < 10 ? `0${seconds}` : seconds;
  const isNegative = milliSeconds < 0;
  return `${isNegative ? "-" : ""}${hours}:${minutes}:${seconds}`;
}

interface Props {
  onSubmit: (data: SeaSurveyFormInputs, files: File[], dirty: boolean) => void;
  readOnly?: boolean;
  small?: boolean;
  defaultValues?: SeaSurveyDetailsDto;
  formId: string;
}

const SeaSurveyForm = ({
  onSubmit,
  readOnly,
  small,
  formId,
  defaultValues,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("sea-survey");

  const { FileExplorerDialog, setFileDialogOpen } = useFileExplorerDialog();
  const formDefaultValues = useMemo(
    () => dataToForm(defaultValues),
    [defaultValues],
  );
  const formMethods = useSmartForm<SeaSurveyFormInputs>({
    defaultValues: formDefaultValues,
  });

  React.useEffect(() => {
    formMethods.register("fileIds");
  }, [formMethods]);

  const { getMapAreas } = useMapAreaApi();
  const { getSeaSurveyFiles } = useSeaSurveyApi();
  const [selectedFiles, setSelectedFiles] = useState<ReturnNodeDto[]>();
  const [surveyFiles, setSurveyFiles] = useState<ReturnNodeDto[]>();
  const [files, setFiles] = React.useState<File[]>([]);

  const { show } = usePopup();

  const internalOnSubmit = useCallback(
    (d: SeaSurveyFormInputs) => {
      onSubmit(
        { ...d, fileCount: (files.length ?? 0) + (d.fileIds?.length ?? 0) },
        files,
        formMethods.formState.isDirty,
      );
    },
    [files, formMethods.formState.isDirty, onSubmit],
  );
  const { isLoading: isLoadingAreas, data: mapAreas } = useCustomQuery(
    "map-areas",
    () => getMapAreas(),
  );

  // Refetch file statuses
  useCustomQuery(
    { key: "sea-survey-files", id: defaultValues?.id },
    () => getSeaSurveyFiles(defaultValues!.id),
    {
      enabled: !!defaultValues?.id,
      onSuccess: (d) => setSurveyFiles(d ?? undefined),
      refetchInterval: defaultValues?.id && !readOnly ? false : 5000,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (!defaultValues || !defaultValues.files) {
      setSelectedFiles(undefined);
      return;
    }
    setSelectedFiles(
      defaultValues.files.map((f) => ({
        ...f,
        fullName: `${f.name}${f.fileExtension}`,
      })),
    );
  }, [defaultValues]);

  const getFileStatus = useCallback(
    (file: ReturnNodeDto): JSX.Element | null => {
      const node = surveyFiles?.find((f) => f.id === file.id);
      if (!node?.queuedFile?.state?.name) return null;
      const fileId = node.queuedFile.state.id;
      const hasError = fileId === FileStateEnum.Error;
      const isDone = fileId === FileStateEnum.Done;

      let queueStep = isDone ? null : (
        <Typography sx={{ margin: "auto 0" }} variant="body2">
          {t(`file-queue-step:${node.queuedFile.processStep?.name}`)}
        </Typography>
      );
      if (hasError)
        queueStep = (
          <Typography variant="body2" noWrap>
            {node.queuedFile.error}
          </Typography>
        );

      return (
        <Tooltip title={t("file-status").toString()}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography fontWeight="700" marginRight="5px" variant="body2">
              {t(`file-state:${node.queuedFile.state?.name}`)}
              {isDone && !hasError ? "" : ":"}
            </Typography>
            {queueStep}
          </Box>
        </Tooltip>
      );
    },
    [surveyFiles, t],
  );

  const getFileProgress = useCallback(
    (file: ReturnNodeDto): JSX.Element | null => {
      const node = surveyFiles?.find((f) => f.id === file.id);
      if (!node?.queuedFile?.startTime || !node?.queuedFile.state) return null;
      const startDate = new Date(node.queuedFile?.startTime);
      const estimatedSeconds = node.queuedFile.estimatedRunTimeMs;
      const queuedFileId = node.queuedFile.state.id;
      const estimatedDate = new Date(
        startDate.getTime() + estimatedSeconds,
      ).valueOf();
      const currentTime = Date.now();
      const timeLeft = estimatedDate - currentTime;

      const percentage = Math.max(
        Math.min(100 - (timeLeft / estimatedSeconds) * 100, 100),
        0,
      );
      const showProgress = queuedFileId === FileStateEnum.Processing;

      if (!showProgress) return null;

      return (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          {timeLeft > 0 ? (
            <Box sx={{ width: "100%", display: "flex" }}>
              <Typography
                fontWeight="700"
                marginRight="5px"
                variant="body2"
                noWrap
              >
                {t("file-queue-step:EstimatedDuration")}:
              </Typography>
              <Typography variant="body2" noWrap>
                {msToHMS(timeLeft)}
              </Typography>
            </Box>
          ) : null}
          <LinearProgressWithLabel value={percentage} />
        </Box>
      );
    },
    [surveyFiles, t],
  );

  const { user } = useAuth();
  const canAccessArchived = React.useMemo(
    () => !!(user?.isAdmin || user?.isOrganizationAdmin),
    [user],
  );

  if (isLoadingAreas) return <LoadingBox />;

  return (
    <Container maxWidth={false}>
      <SmartForm<SeaSurveyFormInputs>
        onSubmit={internalOnSubmit}
        gridProps={{ justifyContent: "center" }}
        methods={formMethods}
        formId={formId}
      >
        <SmartTextField<SeaSurveyFormInputs>
          name="name"
          label={t("name")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          required
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="description"
          label={t("description")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartDatePicker<SeaSurveyFormInputs>
          name="surveyStartTimestamp"
          controllerProps={{
            rules: { required: { value: true, message: t("form:required") } },
          }}
          label={t("start-timestamp")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          textfieldProps={{ fullWidth: true }}
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartDatePicker<SeaSurveyFormInputs>
          name="surveyEndTimestamp"
          controllerProps={{
            rules: { required: { value: true, message: t("form:required") } },
          }}
          label={t("end-timestamp")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          textfieldProps={{ fullWidth: true }}
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="surveyor"
          label={t("surveyor")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          disabled={readOnly}
          fullWidth
        />
        <SmartTextField<SeaSurveyFormInputs>
          fullWidth
          disabled={readOnly}
          name="vessel"
          label={t("vessel")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="sizeSquareMeters"
          label={t("size")}
          type="number"
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<SeaSurveyFormInputs>
          fullWidth
          disabled={readOnly}
          name="customer"
          label={t("customer")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="contact"
          label={t("contact")}
          disabled={readOnly}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
        />
        <SurveyMethodAutocomplete<SeaSurveyFormInputs>
          name="surveyMethodId"
          readOnly={readOnly}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          formMethods={formMethods}
        />
        <ComparisonLevelAutocomplete<SeaSurveyFormInputs>
          name="comparisonLevelId"
          readOnly={readOnly}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          formMethods={formMethods}
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="comparisonLevelOffsetMm"
          label={t("comparison-level-offset-mm")}
          disabled={readOnly}
          InputProps={{ endAdornment: "mm" }}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
        />
        <CoordinateSystemAutocomplete<SeaSurveyFormInputs>
          name="coordinateSystemId"
          readOnly={readOnly}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          formMethods={formMethods}
          required
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="projectManager"
          label={t("project-manager")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          disabled={readOnly}
          fullWidth
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="designer"
          label={t("handler")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          disabled={readOnly}
          fullWidth
        />
        <SmartTextField<SeaSurveyFormInputs>
          name="expert"
          label={t("expert")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          disabled={readOnly}
          fullWidth
        />
        <SecurityClassificationAutocomplete<SeaSurveyFormInputs>
          name="securityClassificationId"
          readOnly={readOnly}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          formMethods={formMethods}
        />
        <SmartDatePicker<SeaSurveyFormInputs>
          name="releaseTimestamp"
          label={t("release-timestamp")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          textfieldProps={{ fullWidth: true }}
          clearable
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <GridWrapper noGrid>
          <Grid item container xs={12} alignItems="center">
            <Grid item xs={7}>
              <SmartAutoComplete<SeaSurveyFormInputs, number, true>
                name="mapAreaIds"
                label={t("map-areas")}
                options={mapAreas?.results.map((x) => x.mapAreaId) ?? []}
                // gridProps={{ xs: small ? 8 : 9 }}
                fullWidth
                multiple
                controllerProps={{
                  rules: {
                    required: { value: true, message: t("form:required") },
                  },
                }}
                disabled={isLoadingAreas || readOnly}
                getOptionLabel={(opt) => {
                  if (!(typeof opt === "number")) return "";
                  const ma = mapAreas?.results.find((x) => x.mapAreaId === opt);
                  if (!ma) return t("general:unknown");
                  return ma.mapAreaType === MapAreaTypeEnum.Project
                    ? `${ma.projectID ? `${ma.projectID} -` : ""} ${ma.name}`
                    : ma.name;
                }}
              />
            </Grid>
            <Grid item xs={2} sx={{ ml: 2 }}>
              <SmartCheckbox<SeaSurveyFormInputs>
                name="merge"
                label={t("merge-files")}
                disabled={isLoadingAreas || readOnly}
              />
            </Grid>
            {canAccessArchived ? (
              <Grid item xs={2} sx={{ ml: 2 }}>
                <SmartCheckbox<SeaSurveyFormInputs>
                  name="archived"
                  label={t("archived")}
                  disabled={isLoadingAreas || readOnly}
                  // gridProps={{ xs: 1 }}
                />
              </Grid>
            ) : null}
          </Grid>
        </GridWrapper>
      </SmartForm>
      <Grid
        container
        wrap="nowrap"
        alignItems="center"
        justifyContent="flex-start"
        style={{ marginTop: 15, maxHeight: 400, overflow: "auto" }}
        direction="column"
      >
        {!readOnly ? (
          <Grid item>
            <Button
              onClick={() => setFileDialogOpen(true)}
              variant="contained"
              color="secondary"
              disabled={readOnly}
            >
              {t("select-existing-point-cloud-file")}
            </Button>
            <Button
              sx={{ marginLeft: 1 }}
              onClick={() =>
                show(
                  ({ close }) => (
                    <FileImportDialog
                      onClose={close}
                      onSubmit={(f) =>
                        setFiles((oldFiles: File[]) => [...oldFiles, ...f])
                      }
                    />
                  ),
                  {
                    dialogProps: { maxWidth: "lg", fullWidth: true },
                  },
                )
              }
              variant="contained"
              color="secondary"
              disabled={readOnly}
            >
              {t("add-new-file")}
            </Button>
          </Grid>
        ) : null}
        {selectedFiles?.length || files.length ? (
          <Grid item xs={10} lg={6} style={{ marginTop: 15, width: "45%" }}>
            <Typography align="center" variant="h6">
              {`${t("selected-file")}:`}
            </Typography>

            <List>
              {selectedFiles?.map((s) => (
                <ListItem sx={{ flexDirection: "column" }} key={s.id}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "column",
                      width: "100%",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          fontWeight="700"
                          noWrap
                          title={s.fullName}
                        >
                          {s.fullName}
                        </Typography>
                      }
                      secondary={defaultValues ? getFileStatus(s) : null}
                      disableTypography
                      // style={{ marginRight: "5px" }}
                    />
                    <ListItemIcon
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "flex-start",
                      }}
                    >
                      <IconButton
                        disabled={readOnly}
                        onClick={() => {
                          const filtered = selectedFiles.filter(
                            (f) => f.id !== s.id,
                          );
                          formMethods.setValue(
                            "fileIds",
                            filtered.map((f) => f.id),
                            { shouldDirty: true, shouldTouch: true },
                          );
                          setSelectedFiles(filtered);
                        }}
                        size="large"
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItemIcon>
                  </Box>

                  {getFileProgress(s)}
                </ListItem>
              ))}
              {files?.map((s) => (
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                <ListItem key={s.name + s.size}>
                  <ListItemText
                    primary={
                      <Typography variant="body1" noWrap title={s.name}>
                        {s.name}
                      </Typography>
                    }
                    disableTypography
                    style={{ marginRight: 5 }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      disabled={readOnly}
                      onClick={() => {
                        setFiles(files.filter((f) => f !== s));
                      }}
                      size="large"
                    >
                      <CloseIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        ) : null}
      </Grid>

      <FileExplorerDialog>
        <FileExplorer
          onClose={() => setFileDialogOpen(false)}
          onFileSelected={(sfiles) => {
            formMethods.setValue(
              "fileIds",
              sfiles.map((f) => f.id),
              { shouldDirty: true, shouldTouch: true },
            );
            setSelectedFiles((oldFiles) => [...(oldFiles ?? []), ...sfiles]);
            setFileDialogOpen(false);
          }}
        />
      </FileExplorerDialog>
    </Container>
  );
};

export default SeaSurveyForm;
